import { useLocation, useMatch, useNavigate, useParams } from 'react-router-dom'
import { MenuItem, TextField } from '@mui/material'
import {
  DashboardOutlined,
  BarChartOutlined,
  CheckCircleOutline,
  CameraAltOutlined,
  TrendingUpOutlined,
  VerifiedOutlined,
  SwapHorizOutlined,
  AutorenewOutlined,
  LanOutlined,
  CallSplitOutlined,
  SpeedOutlined,
  Inventory2Outlined,
  AccountTreeOutlined,
  CategoryOutlined,
  SyncAltOutlined,
  TableChartOutlined,
  WarehouseOutlined,
} from '@mui/icons-material'
import { Sidebar, SidebarGroup } from 'Common/Layout/Sidebar'
import { useProjectData } from 'Features/Projects/Scenarios/Scenarios.data'

const PROJECT_SECTIONS = [
  { label: 'Scenarios', path: 'scenarios', icon: <DashboardOutlined /> },
  { label: 'Analysis', path: 'analysis', icon: <BarChartOutlined /> },
  { label: 'Approval', path: 'approval', icon: <CheckCircleOutline /> },
  { label: 'Snapshot', path: 'snapshot', icon: <CameraAltOutlined /> },
]

/**
 * A scenario's settings, in the order they are worked through. Paths are the
 * child routes of `scenarios/:scenarioId/scenario`. `also` lists the deeper
 * routes that belong to an item, so it stays selected on them.
 */
const SCENARIO_SECTIONS = [
  {
    label: 'Portfolio settings',
    items: [
      { label: 'Sales rate', path: 'sales-rate', icon: <TrendingUpOutlined /> },
      {
        label: 'Service level',
        path: 'service-level',
        icon: <VerifiedOutlined />,
      },
    ],
  },
  {
    label: 'Network settings',
    items: [
      {
        label: 'Site stock flow',
        path: 'site-stock-flow',
        icon: <SwapHorizOutlined />,
      },
      {
        label: 'Replenishment',
        path: 'replenishment',
        icon: <AutorenewOutlined />,
      },
      {
        label: 'Network capacities',
        path: 'network-capacities',
        icon: <LanOutlined />,
      },
    ],
  },
  {
    label: 'Factory settings',
    items: [
      {
        label: 'Allocations',
        path: 'allocations',
        icon: <CallSplitOutlined />,
      },
      { label: 'Capacities', path: 'capacities', icon: <SpeedOutlined /> },
      {
        label: 'Batch production',
        path: 'batch-production',
        icon: <Inventory2Outlined />,
      },
      {
        label: 'SKU production',
        path: 'sku-production',
        icon: <CategoryOutlined />,
      },
      {
        label: 'Total production',
        path: 'total-production',
        icon: <AccountTreeOutlined />,
      },
      { label: 'Changeovers', path: 'changeovers', icon: <SyncAltOutlined /> },
    ],
  },
  {
    label: 'Summary',
    items: [
      {
        label: 'Template summary',
        path: 'template-summary',
        icon: <TableChartOutlined />,
      },
      {
        label: 'Inventory summary',
        path: 'inventory-summary',
        also: 'inventory-results',
        icon: <WarehouseOutlined />,
      },
    ],
  },
]

/**
 * The project's sidebar. Outside a scenario it lists the project's sections.
 * Inside one (`scenarios/:scenarioId/scenario/*`) it becomes the scenario's
 * settings — a scenario switcher, the four numbered settings groups, then the
 * project's sections below them — so the page carries one sidebar, not the
 * project nav plus the 260px settings drawer that used to sit beside it.
 *
 * Every selected state comes from the URL. Both the old nav and the old
 * drawer kept an index in state, so a reload or a deep link lit up the wrong
 * item (or none).
 */
export const NavigationView = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { pathname } = useLocation()
  const scenarioMatch = useMatch(
    '/projects/:projectId/scenarios/:scenarioId/scenario/*'
  )
  const scenarioId = scenarioMatch?.params.scenarioId
  const base = `/projects/${projectId}`

  const { data: projectData } = useProjectData(
    scenarioId ? projectId : undefined
  )

  const projectGroup = (numbered: boolean): SidebarGroup => ({
    label: 'Project',
    numbered,
    items: PROJECT_SECTIONS.map((section) => ({
      label: section.label,
      to: `${base}/${section.path}`,
      icon: section.icon,
      // Outside a scenario, the project's index route and create-scenario
      // belong to Scenarios. Inside one, the settings own the selection.
      active: scenarioId
        ? false
        : pathname.startsWith(`${base}/${section.path}`) ||
          (section.path === 'scenarios' &&
            !PROJECT_SECTIONS.some((other) =>
              pathname.startsWith(`${base}/${other.path}`)
            )),
    })),
  })

  if (!scenarioId) {
    return <Sidebar label="Project sections" groups={[projectGroup(false)]} />
  }

  const scenarioBase = `${base}/scenarios/${scenarioId}/scenario`
  const current = scenarioMatch?.params['*'] ?? ''
  const scenarios = (projectData?.scenariosList ?? []).filter(
    (scenario) => scenario.scenarioName
  )

  const groups: SidebarGroup[] = [
    ...SCENARIO_SECTIONS.map((group) => ({
      label: group.label,
      items: group.items.map((item) => ({
        label: item.label,
        to: `${scenarioBase}/${item.path}`,
        icon: item.icon,
        active:
          current.startsWith(item.path) ||
          ('also' in item && current.startsWith(item.also as string)),
      })),
    })),
    projectGroup(false),
  ]

  const switcher = (
    <TextField
      select
      fullWidth
      size="small"
      label="Scenario"
      value={
        scenarios.some((s) => String(s.scenarioId) === scenarioId)
          ? scenarioId
          : ''
      }
      disabled={!scenarios.length}
      onChange={(event) =>
        navigate(
          `${base}/scenarios/${event.target.value}/scenario/template-summary`
        )
      }
    >
      {scenarios.map((scenario) => (
        <MenuItem key={scenario.scenarioId} value={String(scenario.scenarioId)}>
          {scenario.scenarioName}
        </MenuItem>
      ))}
    </TextField>
  )

  return <Sidebar label="Scenario settings" header={switcher} groups={groups} />
}
